/**
 * Der Feldkatalog der KA-Aufnahme: jedes Feld des amtlichen Aufnahmeformblatts
 * KA 6, mit seiner Nummer, seinem Namen und seinem Platz im Datenmodell.
 *
 * Daraus sollen sich später Maske, Druck, Export, Pflichtfeldprüfung und die
 * Klartexte der Konfliktklärung speisen, statt dass jede dieser Stellen die
 * Felder noch einmal aufzählt.
 *
 * ## Was belegt ist und was nicht
 *
 * **Belegt** (Quelle: "Aufnahmeformblatt KA 6", frei herunterladbar unter
 * www.infogeo.de/KA6, gelesen am 19.09.2026 am Bild des Bogens):
 * `nr`, `name`, `block`, `zelle` und die Reihenfolge.
 *
 * - Die Nummern 7 und 8b kommen im Bogen nicht vor.
 * - Wo der Bogen mehrere Felder in EINE Zelle stapelt ("29/32/33" über
 *   "Humusgeh. / Bodenfeuchte / Konsistenz"), steht die Zelle in `zelle`. Die
 *   Zuordnung von Nummer zu Name folgt dort der gedruckten Reihenfolge. Für
 *   29/32/33 deckt sie sich mit den Feldnummern der 5. Auflage; für 36/37/38,
 *   41a/41b/41c und 47/48/49 ist sie nur aus der Reihenfolge geschlossen.
 * - Abkürzungen des Bogens sind nur ausgeschrieben, wo der Klartext belegt ist
 *   ("Humusgeh." = Humusgehalt). "Geog. org. Kohlenstoff" steht wörtlich da.
 *
 * **Eigene Festlegung:** `id` und `pfade` (wo der Wert im Punkt liegt).
 *
 * **Arbeitsannahme, am Buch zu prüfen:** `art` und `mehrfach`. Der Bogen nennt
 * nur Namen und Nummern, nicht die Art der Eingabe. Die Annahmen stützen sich
 * auf den Aufbau des Bogens (je Horizont drei Schreibzeilen) und auf den
 * Tagungsbeitrag von Hesse u. a. (DBG 2022), der zusammengesetzte Merkmale
 * zeigt ("ed,fl(ai),g3,f3,gro5"). Sie legen nichts Fachliches fest.
 *
 * **Diese Datei enthält keine Schlüssellisten** (siehe `ka-listen.ts`).
 */

export type KaBlock = 'titel' | 'situation' | 'horizont1' | 'horizont2' | 'profil';

/**
 * Art der Eingabe.
 * - `liste`: ein Kurzzeichen aus einer Schlüsselliste
 * - `merkmal`: aus mehreren Teillisten zusammengesetzt (Art, Form, Ausprägung, Anteil, Größe)
 * - `symbol`: das Horizontsymbol, aus Haupt- und Zusatzsymbolen nach Regeln gebildet
 * - `farbe`: Farbe nach Farbtafel
 */
export type KaEingabeart = 'text' | 'zahl' | 'datum' | 'liste' | 'merkmal' | 'symbol' | 'farbe';

export interface KaFeld {
	/** Eigener Schlüssel, eindeutig im Katalog. */
	id: string;
	/** Feldnummer, wie sie im Bogen gedruckt ist. Die beiden Proben-Spalten tragen keine. */
	nr?: string;
	/** Beschriftung im Bogen. */
	name: string;
	block: KaBlock;
	/** Gestapelte Zelle, wie gedruckt, z. B. "29/32/33". */
	zelle?: string;
	/** Wo der Wert im Punkt liegt. `[]` steht für "je Horizontzeile". */
	pfade: string[];
	art: KaEingabeart;
	/** Mehrere Einträge je Horizont. */
	mehrfach?: boolean;
}

/**
 * Felder, die NICHT im KA-Teil liegen, sondern an ihrer bisherigen Stelle im
 * Punkt: Karte, Listen, Suche, Gastmodus und Workflow lesen sie dort, für jedes
 * Verfahren gleich.
 */
export const GEMEINSAME_PFADE: readonly string[] = [
	'header.nummer',
	'header.aufnahme.datum',
	'header.aufnahme.kartierer',
	'header.aufnahme.tiefe',
	'header.koordinaten.x',
	'header.koordinaten.y',
	'header.bemerkungen',
];

const H = 'ka.horizonte[].';

export const KA6_FELDER: readonly KaFeld[] = [
	// ---- Titeldaten ----
	{ id: 'tkNr', nr: '1', name: 'TK-Nr.', block: 'titel', pfade: ['ka.titel.tkNr'], art: 'text' },
	{ id: 'projektNr', nr: '2', name: 'Projekt-Nr.', block: 'titel', pfade: ['ka.titel.projektNr'], art: 'text' },
	{ id: 'profilNr', nr: '3', name: 'Profil-Nr.', block: 'titel', pfade: ['header.nummer'], art: 'text' },
	{ id: 'datum', nr: '4', name: 'Datum d. Aufnahme', block: 'titel', pfade: ['header.aufnahme.datum'], art: 'datum' },
	{ id: 'bearbeiter', nr: '5', name: 'Bearbeiter/in', block: 'titel', pfade: ['header.aufnahme.kartierer'], art: 'text' },
	{ id: 'ostwert', nr: '6a', name: 'Ostwert', block: 'titel', pfade: ['header.koordinaten.x'], art: 'zahl' },
	{ id: 'nordwert', nr: '6b', name: 'Nordwert', block: 'titel', pfade: ['header.koordinaten.y'], art: 'zahl' },
	{ id: 'hoeheNhn', nr: '8a', name: 'Höhe m (NHN)', block: 'titel', pfade: ['ka.titel.hoeheNhn'], art: 'zahl' },
	{ id: 'aufschlussart', nr: '9', name: 'Aufschlussart/Intensität', block: 'titel', pfade: ['ka.titel.aufschlussart'], art: 'liste' },
	{ id: 'aufnahmetiefe', nr: '10', name: 'Aufnahmetiefe', block: 'titel', pfade: ['header.aufnahme.tiefe'], art: 'zahl' },
	{ id: 'titelBemerkungen', nr: '11', name: 'Bemerkungen', block: 'titel', pfade: ['header.bemerkungen'], art: 'text' },

	// ---- Aufnahmesituation ----
	{ id: 'hangneigung', nr: '12', name: 'Hangneigung', block: 'situation', pfade: ['ka.situation.hangneigung'], art: 'liste' },
	{ id: 'exposition', nr: '13', name: 'Exposition', block: 'situation', pfade: ['ka.situation.exposition'], art: 'liste' },
	{ id: 'reliefformtyp', nr: '14', name: 'Reliefformtyp', block: 'situation', pfade: ['ka.situation.reliefformtyp'], art: 'liste' },
	{ id: 'lageImRelief', nr: '15', name: 'Lage im Relief', block: 'situation', pfade: ['ka.situation.lageImRelief'], art: 'liste' },
	{ id: 'mikrorelief', nr: '16', name: 'Mikrorelief', block: 'situation', pfade: ['ka.situation.mikrorelief'], art: 'liste' },
	{ id: 'bodenabtragAuftragNatuerlich', nr: '17a', name: 'Bodenabtrag/-auftrag natürlich', block: 'situation', pfade: ['ka.situation.bodenabtragAuftragNatuerlich'], art: 'liste' },
	{ id: 'bodenabtragAuftragKuenstlich', nr: '17b', name: 'Bodenabtrag/-auftrag künstlich', block: 'situation', pfade: ['ka.situation.bodenabtragAuftragKuenstlich'], art: 'liste' },
	{ id: 'nutzungsart', nr: '18', name: 'Nutzungsart', block: 'situation', pfade: ['ka.situation.nutzungsart'], art: 'liste' },
	{ id: 'vegetation', nr: '19', name: 'Vegetation', block: 'situation', pfade: ['ka.situation.vegetation'], art: 'liste' },
	{ id: 'witterung', nr: '20', name: 'Witterung', block: 'situation', pfade: ['ka.situation.witterung'], art: 'liste' },
	{ id: 'anthropogeneVeraenderungen', nr: '21', name: 'anthropogene Veränderungen', block: 'situation', pfade: ['ka.situation.anthropogeneVeraenderungen'], art: 'liste' },
	{ id: 'bodenoberflaeche', nr: '22', name: 'Bodenoberfläche', block: 'situation', pfade: ['ka.situation.bodenoberflaeche'], art: 'liste' },
	{ id: 'versiegelungsart', nr: '23', name: 'Versiegelungsart', block: 'situation', pfade: ['ka.situation.versiegelungsart'], art: 'liste' },
	{ id: 'regenwuermer', nr: '24', name: 'Regenwürmer', block: 'situation', pfade: ['ka.situation.regenwuermer'], art: 'liste' },
	{ id: 'situationBemerkungen', nr: '25', name: 'Bemerkungen', block: 'situation', pfade: ['ka.situation.bemerkungen'], art: 'text' },

	// ---- Horizontbezogene Daten I (Reihenfolge wie im Bogen: das Symbol steht am Ende) ----
	{ id: 'grenzen', nr: '26', name: 'Unter-/Obergrenze', block: 'horizont1', pfade: [H + 'obergrenze', H + 'untergrenze'], art: 'zahl' },
	{ id: 'grenze', nr: '27', name: 'Form, Schärfe, Lage', block: 'horizont1', pfade: [H + 'grenze'], art: 'merkmal' },
	{ id: 'farbe', nr: '28', name: 'Farbe der Bodenmatrix', block: 'horizont1', pfade: [H + 'farbe'], art: 'farbe' },
	{ id: 'humusgehalt', nr: '29', name: 'Humusgehalt', block: 'horizont1', zelle: '29/32/33', pfade: [H + 'humusgehalt'], art: 'liste' },
	{ id: 'bodenfeuchte', nr: '32', name: 'Bodenfeuchte', block: 'horizont1', zelle: '29/32/33', pfade: [H + 'bodenfeuchte'], art: 'liste' },
	{ id: 'konsistenz', nr: '33', name: 'Konsistenz', block: 'horizont1', zelle: '29/32/33', pfade: [H + 'konsistenz'], art: 'liste' },
	{ id: 'oxidationsmerkmale', nr: '30', name: 'Oxidationsmerkmale', block: 'horizont1', pfade: [H + 'oxidationsmerkmale'], art: 'merkmal', mehrfach: true },
	{ id: 'reduktionsmerkmale', nr: '31', name: 'Reduktionsmerkmale', block: 'horizont1', pfade: [H + 'reduktionsmerkmale'], art: 'merkmal', mehrfach: true },
	{ id: 'weitereMerkmale', nr: '34', name: 'weitere pedogene Merkmale', block: 'horizont1', pfade: [H + 'weitereMerkmale'], art: 'merkmal', mehrfach: true },
	{ id: 'gefuegeform', nr: '35', name: 'Gefügeform', block: 'horizont1', pfade: [H + 'gefuegeform'], art: 'liste' },
	{ id: 'lagerungsart', nr: '36', name: 'Lagerungsart', block: 'horizont1', zelle: '36/37/38', pfade: [H + 'lagerungsart'], art: 'liste' },
	{ id: 'lagerungsform', nr: '37', name: 'Lagerungsform', block: 'horizont1', zelle: '36/37/38', pfade: [H + 'lagerungsform'], art: 'liste' },
	{ id: 'verfestigungsgrad', nr: '38', name: 'Verfestigungsgrad', block: 'horizont1', zelle: '36/37/38', pfade: [H + 'verfestigungsgrad'], art: 'liste' },
	{ id: 'hohlraeume', nr: '39', name: 'Hohlräume (Sekundärporen)', block: 'horizont1', pfade: [H + 'hohlraeume'], art: 'merkmal', mehrfach: true },
	{ id: 'zersetzungsstufe', nr: '40', name: 'Zersetzungsstufe/Humifizierungsgrad', block: 'horizont1', pfade: [H + 'zersetzungsstufe'], art: 'liste' },
	{ id: 'durchwurzelungsintensitaet', nr: '41a', name: 'Durchwurzelungsintensität', block: 'horizont1', zelle: '41a/41b/41c', pfade: [H + 'durchwurzelungsintensitaet'], art: 'liste' },
	{ id: 'wurzelverteilung', nr: '41b', name: 'Wurzelverteilung', block: 'horizont1', zelle: '41a/41b/41c', pfade: [H + 'wurzelverteilung'], art: 'liste' },
	{ id: 'pilzmycel', nr: '41c', name: 'Pilzmycel', block: 'horizont1', zelle: '41a/41b/41c', pfade: [H + 'pilzmycel'], art: 'liste' },
	{ id: 'packungsdichte', nr: '42', name: 'Packungsdichte', block: 'horizont1', pfade: [H + 'packungsdichte'], art: 'liste' },
	{ id: 'horizontsymbol', nr: '58', name: 'Horizontsymbol', block: 'horizont1', pfade: [H + 'horizontsymbol'], art: 'symbol' },

	// ---- Horizontbezogene Daten II (Reihenfolge wie im Bogen: die Substratart steht am Ende) ----
	{ id: 'feinbodenart', nr: '44', name: 'Feinbodenart/Torfart/Muddeart/Streuart', block: 'horizont2', pfade: [H + 'feinbodenart'], art: 'liste' },
	{ id: 'grobbodenfraktionen', nr: '45', name: 'Grobbodenfraktion u. Anteilsklasse', block: 'horizont2', pfade: [H + 'grobbodenfraktionen'], art: 'merkmal', mehrfach: true },
	{ id: 'summeGrobboden', nr: '46', name: 'Summe Grobboden (%)', block: 'horizont2', pfade: [H + 'summeGrobboden'], art: 'zahl' },
	{ id: 'grobbodenanbindung', nr: '47', name: 'Grobbodenanbindung', block: 'horizont2', zelle: '47/48/49', pfade: [H + 'grobbodenanbindung'], art: 'liste' },
	{ id: 'carbonatgehalt', nr: '48', name: 'Carbonatgehalt', block: 'horizont2', zelle: '47/48/49', pfade: [H + 'carbonatgehalt'], art: 'liste' },
	{ id: 'geogOrgKohlenstoff', nr: '49', name: 'Geog. org. Kohlenstoff', block: 'horizont2', zelle: '47/48/49', pfade: [H + 'geogOrgKohlenstoff'], art: 'liste' },
	{ id: 'bodenausgangsgestein', nr: '50', name: 'Bodenausgangsgestein', block: 'horizont2', pfade: [H + 'bodenausgangsgestein'], art: 'liste' },
	{ id: 'geogenese', nr: '43', name: 'Geogenese', block: 'horizont2', pfade: [H + 'geogenese'], art: 'liste' },
	{ id: 'periglaziaereLage', nr: '51', name: 'Periglaziäre Lagen', block: 'horizont2', pfade: [H + 'periglaziaereLage'], art: 'liste' },
	{ id: 'stratigraphie', nr: '55', name: 'Stratigraphie', block: 'horizont2', pfade: [H + 'stratigraphie'], art: 'liste' },
	{ id: 'grobkomponenten', nr: '52a', name: 'Grobkomponenten', block: 'horizont2', pfade: [H + 'grobkomponenten'], art: 'liste', mehrfach: true },
	{ id: 'feinkomponenten', nr: '52b', name: 'Feinkomponenten', block: 'horizont2', pfade: [H + 'feinkomponenten'], art: 'liste', mehrfach: true },
	{ id: 'beimengungen', nr: '53', name: 'Beimengungen', block: 'horizont2', pfade: [H + 'beimengungen'], art: 'liste', mehrfach: true },
	{ id: 'besondereStrukturen', nr: '54', name: 'Besondere Strukturen', block: 'horizont2', pfade: [H + 'besondereStrukturen'], art: 'liste', mehrfach: true },
	{ id: 'geruch', nr: '56', name: 'Geruch', block: 'horizont2', pfade: [H + 'geruch'], art: 'liste' },
	{ id: 'horizontBemerkungen', nr: '57', name: 'Bemerkungen', block: 'horizont2', pfade: [H + 'bemerkungen'], art: 'text' },
	{ id: 'substratart', nr: '59', name: 'Substratart', block: 'horizont2', pfade: [H + 'substratart'], art: 'text' },
	{ id: 'probenGestoert', name: 'Nr. gestörte Proben', block: 'horizont2', pfade: [H + 'probenGestoert'], art: 'text' },
	{ id: 'probenUngestoert', name: 'Nr. ungestörte Proben', block: 'horizont2', pfade: [H + 'probenUngestoert'], art: 'text' },

	// ---- Profilkennzeichnung ----
	{ id: 'bodensystematischeEinheit', nr: '60', name: 'Bodensystematische Einheit', block: 'profil', pfade: ['ka.profil.bodensystematischeEinheit'], art: 'liste' },
	{ id: 'substratsystematischeEinheit', nr: '61', name: 'Substratsystematische Einheit', block: 'profil', pfade: ['ka.profil.substratsystematischeEinheit'], art: 'text' },
	{ id: 'bodenausgangsgesteinsfolge', nr: '62', name: 'Bodenausgangsgesteinsfolge', block: 'profil', pfade: ['ka.profil.bodenausgangsgesteinsfolge'], art: 'text' },
	{ id: 'humusform', nr: '63', name: 'Humusform', block: 'profil', pfade: ['ka.profil.humusform'], art: 'liste' },
	{ id: 'hydrogenetischerMoortyp', nr: '64', name: 'Hydrogenetischer Moortyp', block: 'profil', pfade: ['ka.profil.hydrogenetischerMoortyp'], art: 'liste' },
	{ id: 'durchwurzelbarerBodenraum', nr: '65', name: 'Durchwurzelbarer Bodenraum', block: 'profil', pfade: ['ka.profil.durchwurzelbarerBodenraum'], art: 'liste' },
	{ id: 'wasserstand', nr: '66', name: 'Wasserstand u. GOF', block: 'profil', pfade: ['ka.profil.wasserstand'], art: 'zahl' },
	{ id: 'grundnaessestufe', nr: '67', name: 'Grundnässestufe', block: 'profil', pfade: ['ka.profil.grundnaessestufe'], art: 'liste' },
	{ id: 'besondereWasserverhaeltnisse', nr: '68', name: 'Besondere Wasserverhältnisse', block: 'profil', pfade: ['ka.profil.besondereWasserverhaeltnisse'], art: 'liste' },
	{ id: 'stauHaftnaessestufe', nr: '69', name: 'Stau-/Haftnässestufe', block: 'profil', pfade: ['ka.profil.stauHaftnaessestufe'], art: 'liste' },
	{ id: 'erosionsgrad', nr: '70', name: 'Erosionsgrad', block: 'profil', pfade: ['ka.profil.erosionsgrad'], art: 'liste' },
	{ id: 'profilBemerkungen', nr: '71', name: 'Bemerkungen', block: 'profil', pfade: ['ka.profil.bemerkungen'], art: 'text' },
];

/** Das Feld mit dieser Kennung, oder `undefined`. */
export function kaFeld(id: string): KaFeld | undefined {
	return KA6_FELDER.find(f => f.id === id);
}

/** Alle Felder eines Blocks, in der Reihenfolge des Bogens. */
export function kaFelderImBlock(block: KaBlock): KaFeld[] {
	return KA6_FELDER.filter(f => f.block === block);
}

/**
 * Die Felder der KURZAUFNAHME, **vorläufig**.
 *
 * Die 5. Auflage nennt einen "verkürzten Datensatz": die aus fachlicher Sicht
 * mindestens zu erhebenden Merkmale (dort Abschnitt 5.2, im Bogen grau unterlegt).
 * Die Liste hier überträgt diese Felder auf die Felder des KA6-Bogens. Sie ist ein
 * Vorschlag für den Umfang der ersten Maske, keine Aussage über die 6. Auflage:
 * Wie die KA6 den Mindestumfang fasst, steht im Buch und ist nicht geprüft.
 *
 * Übertragung (5. Auflage → KA6-Bogen): 3→3, 4→4, 6→6a, 7→6b, 9→9, 11→12, 14→14,
 * 19→18, 25→26, 27→58, 28→28, 29→29, 30→30, 31→31, 40→40, 43→43, 44a→44, 44b→45,
 * 44c→46, 45→49, 46→48, 47a→50, 50→60, 51→61, 53b→66.
 *
 * Offen: Feld 40 der 5. Auflage fasste "Lagerungsdichte/Substanzvolumen/
 * Zersetzungsstufe" zusammen. Was im KA6-Bogen an die Stelle der Lagerungsdichte
 * tritt, ist nicht belegt; hier steht deshalb nur die Zersetzungsstufe.
 */
export const KA6_KURZAUFNAHME_VORLAEUFIG: readonly string[] = [
	'profilNr', 'datum', 'ostwert', 'nordwert', 'aufschlussart',
	'hangneigung', 'reliefformtyp', 'nutzungsart',
	'grenzen', 'horizontsymbol', 'farbe', 'humusgehalt',
	'oxidationsmerkmale', 'reduktionsmerkmale', 'zersetzungsstufe',
	'geogenese', 'feinbodenart', 'grobbodenfraktionen', 'summeGrobboden',
	'geogOrgKohlenstoff', 'carbonatgehalt', 'bodenausgangsgestein',
	'bodensystematischeEinheit', 'substratsystematischeEinheit', 'wasserstand',
];
